/**
 * SettingsPlaceholder Component
 * 
 * Placeholder content for settings sections during Phase 1 implementation.
 * Shows section information and indicates future implementation status.
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import { Card, CardBody, Chip } from '@enfomentis/ui';

type SettingsSection =
  | 'app-preferences'
  | 'trading-workflow'
  | 'trading-strategies'
  | 'trading-portfolio'
  | 'profile-settings';

export interface SettingsPlaceholderProps {
  /** Settings section identifier */
  section: SettingsSection;
}

interface SectionInfo {
  title: string;
  description: string;
  status: 'ready' | 'future';
  schemas: string[];
  features: string[];
}

const sectionData: Record<SettingsSection, SectionInfo> = {
  'app-preferences': {
    title: 'App Preferences',
    description: 'Core application configuration and user preferences',
    status: 'ready',
    schemas: ['BasicSettings'],
    features: [
      'Language & display preferences',
      'Timezone configuration', 
      'Time format (12h/24h)',
      'Week start day',
      'Trading days selection',
      'Trading sessions management',
      'Instrument catalog sync preferences'
    ]
  },
  'trading-workflow': {
    title: 'Trading Workflow',
    description: 'Configure your complete trading workflow from pre-market to post-trading',
    status: 'ready', 
    schemas: ['PreTradingSettings', 'LiveTradingSettings', 'PostTradingSettings'],
    features: [
      'Pre-trading ritual configuration',
      'Health metrics tracking setup',
      'Trading objectives management', 
      'Dashboard display preferences',
      'Note categories and quick actions',
      'Economic alerts configuration',
      'Post-trading review settings',
      'AI export preferences',
      'Daily reflection prompts'
    ]
  },
  'trading-strategies': {
    title: 'Trading Strategies',
    description: 'Manage your trading strategies, playbooks, and decision criteria',
    status: 'ready',
    schemas: ['Strategy', 'Playbook', 'PlaybookCriteriaCategory', 'PlaybookCriteriaItem', 'EntryModel', 'OpportunitySettings'],
    features: [
      'Strategy management and organization',
      'Playbook configuration and templates',
      'Criteria categories and items',
      'Entry model definitions',
      'Opportunity tracking settings',
      'No-trade reason categorization',
      'Strategy performance analytics setup'
    ]
  },
  'trading-portfolio': {
    title: 'Trading Portfolio',
    description: 'Manage trading accounts, risk profiles, and broker configurations',
    status: 'ready',
    schemas: ['Account', 'AccountRisk', 'BalanceHistory', 'Broker', 'Instrument'],
    features: [
      'Trading account setup and management',
      'Risk profile configuration',
      'Risk cascade rules',
      'Balance history tracking',
      'Broker account integration',
      'Fee structure configuration',
      'Instrument specifications',
      'Account performance tracking'
    ]
  },
  'profile-settings': {
    title: 'Profile Settings',
    description: 'User profile, license management, and data operations',
    status: 'future',
    schemas: [],
    features: [
      'User profile management',
      'License and subscription info',
      'Data export and import',
      'Account security settings',
      'Privacy preferences',
      'Billing and subscription management'
    ]
  }
};

/**
 * Settings Section Placeholder
 * 
 * Displays detailed information about each settings section including:
 * - Implementation status (ready vs future)
 * - Associated database schemas
 * - Feature list for the section
 * - Development timeline indication
 */
export const SettingsPlaceholder: React.FC<SettingsPlaceholderProps> = ({
  section
}) => {
  const info = sectionData[section];

  return (
    <div className="max-w-4xl space-y-6">
      {/* Section Overview */}
      <Card>
        <CardBody className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary mb-2">
                {info.title}
              </h2>
              <p className="text-text-secondary">
                {info.description}
              </p>
            </div>
            <Chip 
              color={info.status === 'ready' ? 'success' : 'default'}
              variant="flat"
            >
              {info.status === 'ready' ? 'Schema Ready' : 'Future Implementation'}
            </Chip>
          </div>

          {/* Database Schemas */}
          {info.schemas.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-text-primary mb-3">
                Database Schemas
              </h3>
              <div className="flex flex-wrap gap-2">
                {info.schemas.map((schema) => (
                  <Chip 
                    key={schema}
                    color="primary"
                    variant="flat"
                    className="text-sm"
                  >
                    {schema}.ts
                  </Chip>
                ))}
              </div>
            </div>
          )}

          {/* Features List */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-3">
              Planned Features
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {info.features.map((feature, index) => (
                <li 
                  key={index}
                  className="flex items-start gap-2 text-text-secondary"
                >
                  <span className="text-primary mt-1">•</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardBody>
      </Card>

      {/* Development Status */}
      <Card>
        <CardBody className="p-6">
          <h3 className="text-lg font-medium text-text-primary mb-3">
            Implementation Status
          </h3>
          {info.status === 'ready' ? (
            <div className="space-y-2">
              <p className="text-text-secondary">
                ✅ <strong>Phase 1 Complete:</strong> Navigation and layout structure implemented
              </p>
              <p className="text-text-secondary">
                🔄 <strong>Phase 2 Pending:</strong> Forms and functionality will be implemented in dedicated sub-tasks
              </p>
              <p className="text-text-secondary">
                📊 <strong>Database Ready:</strong> All required schemas are defined and available
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-text-secondary">
                ⏳ <strong>Future Implementation:</strong> This section will be built in later development phases
              </p>
              <p className="text-text-secondary">
                📋 <strong>Schema Status:</strong> Database schemas need to be defined
              </p>
              <p className="text-text-secondary">
                🔮 <strong>Timeline:</strong> Implementation timeline to be determined based on priorities
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};